import React from 'react';
import { Activity, BarChart3, CheckCircle2, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

const SNAPSHOT_DATA = [
  { label: 'Tasks completed', value: '128', sub: '+24 vs yesterday' },
  { label: 'Tickets resolved', value: '47', sub: 'Avg 1.2 min response' },
  { label: 'Pipeline value', value: '\u20B9 38.2L', sub: 'Across 12 deals' },
];

const WEEKLY_DATA = [
  { day: 'Mon', ai: 60, human: 30 },
  { day: 'Tue', ai: 80, human: 45 },
  { day: 'Wed', ai: 55, human: 70 },
  { day: 'Thu', ai: 90, human: 50 },
  { day: 'Fri', ai: 70, human: 65 },
  { day: 'Sat', ai: 85, human: 40 },
  { day: 'Sun', ai: 95, human: 55 },
];

const HEALTH_METRICS = [
  { icon: Activity, label: 'Uptime', val: '99.98%' },
  { icon: Layers, label: 'Active agents', val: '14' },
  { icon: CheckCircle2, label: 'SLA hit rate', val: '98%' },
];

const SnapshotItem = ({ item }) => (
  <div className="rounded-xl border border-[#1AB6FF]/15 bg-[#0a1018] p-4">
    <p className="text-xs text-white/55">{item.label}</p>
    <p className="mt-1 font-display font-bold text-2xl text-white">{item.value}</p>
    <p className="text-[11px] text-[#1AB6FF] mt-0.5">{item.sub}</p>
  </div>
);

const SnapshotPanel = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <p className="font-display font-semibold text-white">Today&rsquo;s Snapshot</p>
      <span className="text-[10px] uppercase tracking-wider text-[#1AB6FF]">Live</span>
    </div>
    {SNAPSHOT_DATA.map((s) => (
      <SnapshotItem key={s.label} item={s} />
    ))}
  </div>
);

const ChartHeader = () => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center gap-2">
      <BarChart3 size={18} className="text-[#1AB6FF]" />
      <p className="font-display font-semibold text-white">Weekly throughput</p>
    </div>
    <div className="flex items-center gap-3 text-[11px] text-white/55">
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#1AB6FF]" /> AI Agents</span>
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-white/30" /> Humans</span>
    </div>
  </div>
);

const ChartBar = ({ data }) => (
  <div className="flex-1 h-full flex flex-col justify-end items-center gap-1">
    <div className="w-full flex gap-1 items-end h-full">
      <div className="flex-1 rounded-t-md bg-gradient-to-t from-[#1AB6FF]/40 to-[#1AB6FF]" style={{ height: `${data.ai}%` }} />
      <div className="flex-1 rounded-t-md bg-white/20" style={{ height: `${data.human}%` }} />
    </div>
    <span className="text-[10px] text-white/45">{data.day}</span>
  </div>
);

const HealthMetric = ({ metric }) => (
  <div className="rounded-lg border border-[#1AB6FF]/15 bg-[#04070d]/60 p-3 flex items-center gap-3">
    <metric.icon size={18} className="text-[#1AB6FF]" />
    <div>
      <p className="text-[10px] uppercase tracking-wider text-white/55">{metric.label}</p>
      <p className="font-display font-semibold text-white text-sm">{metric.val}</p>
    </div>
  </div>
);

const ChartPanel = () => (
  <div className="lg:col-span-2 rounded-xl border border-[#1AB6FF]/15 bg-[#0a1018] p-6">
    <ChartHeader />
    <div className="flex items-end gap-3 h-44">
      {WEEKLY_DATA.map((d) => (
        <ChartBar key={d.day} data={d} />
      ))}
    </div>
    <div className="grid grid-cols-3 gap-3 mt-6">
      {HEALTH_METRICS.map((m) => (
        <HealthMetric key={m.label} metric={m} />
      ))}
    </div>
  </div>
);

const Dashboard = () => (
  <section className="relative py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="text-center mb-16">
        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-tight">
          Your Operations. One Intelligent <span className="gradient-text">Dashboard.</span>
        </h2>
        <p className="mt-5 text-white/55 max-w-2xl mx-auto">
          Track tasks, monitor AI agents and review team performance&mdash;all in a single command centre built for Indian businesses.
        </p>
      </div>

      <div className="card-tech rounded-3xl p-6 lg:p-10">
        <div className="grid lg:grid-cols-3 gap-6">
          <SnapshotPanel />
          <ChartPanel />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/5">
          <p className="text-white/55 text-sm">A unified view of your remote team, AI agents and core KPIs.</p>
          <Link to="/ai-system" className="btn-cyan rounded-full px-5 py-2.5 text-sm font-semibold">Explore AI System</Link>
        </div>
      </div>
    </div>
  </section>
);

export default Dashboard;
